import React from 'react';
import { BSModal } from '~/components/Bootstrap';
import { Modal, Spinner } from 'react-bootstrap';
import { Form, FormGroup, ValidationMessage } from '~/components/Form';
import { ServicesContext } from '~/services';

interface SuggestProviderModalProps {
    planCode?: string | undefined;
}

export function SuggestProviderModal({ planCode }: SuggestProviderModalProps) {
    const services = React.useContext(ServicesContext);
    const [show, setShow] = React.useState(false);
    const [submitting, setSubmitting] = React.useState(false);
    const [submitted, setSubmitted] = React.useState(false);
    const [name, setName] = React.useState('');
    const [org, setOrg] = React.useState('');
    const [phone, setPhone] = React.useState('');
    const [fax, setFax] = React.useState('');
    const [npi, setNpi] = React.useState('');
    const [email, setEmail] = React.useState('');
    const [tin, setTin] = React.useState('');
    const [specialty, setSpecialty] = React.useState('');
    const [invalidNPI, setInvalidNPI] = React.useState(false);

    // remove npi error once input is valid
    React.useEffect(() => {
        if (invalidNPI && npi.match(/^\d{10}$/)) setInvalidNPI(false);
    }, [invalidNPI, npi]);

    async function submit() {
        if (!npi.match(/^\d{10}$/)) {
            setInvalidNPI(true);
            return;
        }
        setInvalidNPI(false);
        setSubmitting(true);
        await services.api.call({
            method: 'post',
            url: '/core-api/referrals/suggest-edit',
            data: {
                newProvider: true,
                name,
                org,
                phone,
                fax,
                npi,
                email,
                planCode,
                tin,
                specialty,
            },
        });
        setSubmitted(true);
        setSubmitting(false);
    }

    function close() {
        setShow(false);
        setSubmitting(false);
        setSubmitted(false);
        setInvalidNPI(false);
        setName('');
        setOrg('');
        setPhone('');
        setFax('');
        setNpi('');
        setEmail('');
        setTin('');
        setSpecialty('');
    }

    return (
        <>
            <button className="link-button" type="button" onClick={() => setShow(true)}>
                Can&apos;t find who you are looking for?
            </button>
            <BSModal
                show={show}
                className="suggest-provider-modal"
                onHide={close}
                aria-labelledby="suggest-provider-title"
            >
                <Modal.Header closeButton>
                    <Modal.Title id="suggest-provider-title" role="heading" aria-level={1}>
                        {submitted ? 'Suggested Provider Received' : 'Suggest a Provider'}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {submitted ? (
                        <div>
                            <p>
                                Your suggested provider has been received and is in review. Changes
                                may take some time to appear on the platform.
                            </p>
                            <p>We appreciate your help in improving our accuracy.</p>
                            <div className="text-center">
                                <i
                                    id="suggestion-success-icon"
                                    className="fa-regular fa-check-circle text-success"
                                />
                            </div>
                        </div>
                    ) : (
                        <>
                            <div className="alert alert-warning modal-alert">
                                If a specialist is unavailable in Zephyr, they may be out of
                                network, require prior authorization, or be exempt from the referral
                                process. To learn more, contact{' '}
                                <a href="mailto:PortalSupport@wellmed.net">
                                    PortalSupport@wellmed.net
                                </a>
                                .
                            </div>
                            <p className="h5">Provider Information</p>
                            <p>
                                Suggest a new provider for our referral network. If verified, they
                                will become a referable option in the future.
                            </p>
                            <Form
                                id="suggest-provider-form"
                                onSubmit={submit}
                                values={{ name, org, phone, fax, npi, email }}
                            >
                                <div className="row">
                                    <FormGroup htmlFor="name" className="col-6">
                                        <label className="form-label">
                                            Provider Name *
                                            <input
                                                id="name"
                                                className="form-control"
                                                value={name}
                                                onChange={(e) => setName(e.target.value)}
                                                required
                                            />
                                        </label>
                                        <ValidationMessage type="required" className="pull-left">
                                            Provider Name is required.
                                        </ValidationMessage>
                                    </FormGroup>
                                    <FormGroup htmlFor="org" className="col-6">
                                        <label className="form-label">
                                            Organization Name
                                            <input
                                                id="org"
                                                className="form-control"
                                                value={org}
                                                onChange={(e) => setOrg(e.target.value)}
                                            />
                                        </label>
                                    </FormGroup>
                                </div>
                                <div className="row">
                                    <FormGroup htmlFor="phone" className="col-6">
                                        <label className="form-label">
                                            Phone *
                                            <input
                                                id="phone"
                                                className="form-control"
                                                value={phone}
                                                onChange={(e) => setPhone(e.target.value)}
                                                required
                                            />
                                        </label>
                                        <ValidationMessage type="required" className="pull-left">
                                            Phone is required.
                                        </ValidationMessage>
                                    </FormGroup>
                                    <FormGroup htmlFor="fax" className="col-6">
                                        <label className="form-label">
                                            Fax *
                                            <input
                                                id="fax"
                                                className="form-control"
                                                value={fax}
                                                onChange={(e) => setFax(e.target.value)}
                                                required
                                            />
                                        </label>
                                        <ValidationMessage type="required" className="pull-left">
                                            Fax is required.
                                        </ValidationMessage>
                                    </FormGroup>
                                </div>
                                <div className="row">
                                    <FormGroup htmlFor="npi" className="col-6">
                                        <label className="form-label">
                                            Provider NPI *
                                            <input
                                                id="npi"
                                                className="form-control"
                                                value={npi}
                                                onChange={(e) => setNpi(e.target.value)}
                                                required
                                                maxLength={10}
                                            />
                                        </label>
                                        <ValidationMessage type="required" className="pull-left">
                                            NPI is required.
                                        </ValidationMessage>
                                        <ValidationMessage
                                            showBeforeSubmit
                                            type="external"
                                            rule={invalidNPI}
                                            className="pull-left"
                                        >
                                            NPI must be a 10 digit number.
                                        </ValidationMessage>
                                    </FormGroup>
                                    <FormGroup htmlFor="email" className="col-6">
                                        <label className="form-label">
                                            Email
                                            <input
                                                id="email"
                                                className="form-control"
                                                value={email}
                                                onChange={(e) => setEmail(e.target.value)}
                                            />
                                        </label>
                                    </FormGroup>
                                </div>
                                <div className="row">
                                    <FormGroup htmlFor="tin" className="col-6">
                                        <label className="form-label">
                                            TIN
                                            <input
                                                id="tin"
                                                className="form-control"
                                                value={tin}
                                                onChange={(e) => setTin(e.target.value)}
                                            />
                                        </label>
                                    </FormGroup>
                                    <FormGroup htmlFor="specialty" className="col-6">
                                        <label className="form-label">
                                            Provider Specialty
                                            <input
                                                id="specialty"
                                                className="form-control"
                                                value={specialty}
                                                onChange={(e) => setSpecialty(e.target.value)}
                                            />
                                        </label>
                                    </FormGroup>
                                </div>
                            </Form>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <button className="btn btn-secondary" type="button" onClick={close}>
                        {submitted ? 'Close' : 'Cancel'}
                    </button>
                    {!submitted && (
                        <button
                            type="submit"
                            form="suggest-provider-form"
                            disabled={submitting}
                            className="btn btn-primary"
                        >
                            {submitting && (
                                <Spinner
                                    animation="border"
                                    variant="light"
                                    size="sm"
                                    role="status"
                                    className="me-1"
                                />
                            )}
                            Submit
                        </button>
                    )}
                </Modal.Footer>
            </BSModal>
        </>
    );
}
